import { z } from "zod";

import { MetadataSchema } from "api/schemas/common";
import type { Location } from "api/schemas/shared/Treatment";
import {
  AilmentSchema,
  LateralLocationSchema,
  LocationSchema,
  TreatmentTypeSchema,
} from "api/schemas/shared/Treatment";
import { pinSchema } from "routes/login/utils/validation/loginBankId";

const GetTherapistInvitationsArgsSchema = z.object({
  params: z.object({
    limit: z.number(),
    page: z.number(),
  }),
});
export type GetTherapistInvitationsArgs = z.infer<typeof GetTherapistInvitationsArgsSchema>;

export const TherapistInvitationSchema = z.object({
  created_at: z.string(),
  email: z.email().nullable(),
  id: z.number(),
  last_reminder_at: z.string().nullable(),
  name: z.string(),
  phone_number: z.string().nullable(),
  reminder_count: z.number(),
  sent_at: z.string().nullable(),
});
export type TherapistInvitation = z.infer<typeof TherapistInvitationSchema>;

export const GetTherapistInvitationsResponseSchema = z.object({
  items: z.array(TherapistInvitationSchema),
  metadata: MetadataSchema,
});

const personalNumberInputSchema = z
  .string()
  .transform(val => val.replace("-", ""))
  .pipe(pinSchema);

export const LATERAL_REQUIRED_LOCATIONS: Location[] = [
  "elbow",
  "foot",
  "hand",
  "hip",
  "knee",
  "lower_leg",
  "shoulder",
  "wrist",
];

interface MakeFastTrackPayloadSchemaOptions {
  requirePersonalNumber?: boolean;
  requireUsState?: boolean;
}

export const makeFastTrackPayloadSchema = (options: MakeFastTrackPayloadSchemaOptions) => {
  const { requirePersonalNumber = false, requireUsState = false } = options;

  return z
    .object({
      ailment: AilmentSchema,
      country_code: z.string(),
      email: z.email(),
      first_name: z.string().min(1),
      language_code: z.string(),
      last_name: z.string().min(1),
      lateral_location: LateralLocationSchema.optional(),
      pain_location: LocationSchema.optional(),
      personal_number: requirePersonalNumber ? personalNumberInputSchema : z.string().optional(),
      phone_country_code: z.string(),
      phone_number: z.string().min(1),
      treatment_type: TreatmentTypeSchema,
      us_state: requireUsState ? z.string().min(1) : z.string().optional(),
    })
    .superRefine((data, ctx) => {
      const noLateral = data.lateral_location === undefined || data.lateral_location === "undefined";
      if (data.pain_location && LATERAL_REQUIRED_LOCATIONS.includes(data.pain_location) && noLateral) {
        ctx.addIssue({
          code: "custom",
          message: "Lateral location is required for the selected pain location",
          path: ["lateral_location"],
        });
      }
    });
};

const FastTrackPayloadSchema = makeFastTrackPayloadSchema({ requirePersonalNumber: false });
export type FastTrackPayload = z.infer<typeof FastTrackPayloadSchema>;

const PostTherapistInvitationArgsSchema = z.object({
  payload: FastTrackPayloadSchema,
});
export type PostTherapistInvitationArgs = z.infer<typeof PostTherapistInvitationArgsSchema>;

export const PostTherapistInvitationResponseSchema = z.object({
  patient_id: z.number(),
});
export type PostTherapistInvitationResponse = z.infer<typeof PostTherapistInvitationResponseSchema>;
